import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TestimonialsSectionProps {
    testimonials: Array<Testimonial>
}

const controlClasses = 'flex size-11 items-center justify-center rounded-full bg-background text-foreground shadow-outline transition-shadow duration-300 hover:shadow-outline-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

export const TestimonialsSection: FC<TestimonialsSectionProps> = ({ testimonials }): ReactNode => {
    const [active, setActive] = useState(0)
    const count = testimonials.length
    const current = Math.min(active, Math.max(count - 1, 0))

    const show = (index: number): void => setActive((index + count) % count)

    return (
        <section id='testimonials' aria-labelledby='testimonials-title' className='w-full bg-background bg-arc px-4 md:px-8'>
            <div className='mx-auto w-full max-w-site py-12 md:py-16 lg:py-20'>
                <div className='mx-auto mb-12 flex max-w-[43rem] flex-col items-center text-center text-balance md:mb-20'>
                    <h2 id='testimonials-title' className='font-heading text-heading-2 font-medium'>
                        Hear What Our Clients Say
                    </h2>

                    <p className='mt-2 text-lead text-muted-foreground'>
                        Our clients share their remarkable experiences working with us, illustrating our commitment to excellence.
                    </p>
                </div>

                {count > 0 && (
                    <div className='relative isolate overflow-hidden rounded-card bg-background bg-linear-to-b from-brand-haze to-transparent shadow-card'>
                        {testimonials.map((testimonial, index) => (
                            <figure
                                key={testimonial.id}
                                hidden={index !== current}
                                aria-roledescription={count > 1 ? 'slide' : undefined}
                                aria-label={count > 1 ? `${index + 1} of ${count}` : undefined}
                                className='grid min-h-[27rem] grid-cols-1 md:grid-cols-[5fr_7fr]'
                            >
                                <div className='relative h-[300px] bg-brand-mist md:h-auto'>
                                    {testimonial.photo && (
                                        <img
                                            src={testimonial.photo}
                                            alt={testimonial.photoAlt ?? ''}
                                            loading='lazy'
                                            decoding='async'
                                            className='absolute inset-0 size-full object-cover'
                                        />
                                    )}
                                </div>

                                <div className='flex flex-col items-start justify-center gap-5 px-6 py-12 animate-in fade-in duration-500 md:px-10 md:py-20 lg:px-20 lg:py-32'>
                                    <blockquote>
                                        <p className='font-heading text-heading-3 font-medium text-balance' data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>
                                            {testimonial.quote}
                                        </p>
                                    </blockquote>

                                    <figcaption className='flex flex-col items-start text-base leading-[1.3]'>
                                        <span data-mdlfy-cms={`testimonials:${testimonial.id}:name`}>
                                            {testimonial.name}
                                        </span>

                                        {testimonial.role && (
                                            <span className='opacity-50' data-mdlfy-cms={`testimonials:${testimonial.id}:role`}>
                                                {testimonial.role}
                                            </span>
                                        )}
                                    </figcaption>
                                </div>
                            </figure>
                        ))}
                    </div>
                )}

                {count > 1 && (
                    <div className='mt-6 flex items-center justify-center gap-4'>
                        <button type='button' onClick={() => show(current - 1)} aria-label='Previous testimonial' className={controlClasses}>
                            <ChevronLeft className='size-5' aria-hidden='true' />
                        </button>

                        <div className='flex items-center gap-2'>
                            {testimonials.map((testimonial, index) => (
                                <button
                                    key={testimonial.id}
                                    type='button'
                                    onClick={() => show(index)}
                                    aria-label={`Show testimonial ${index + 1} of ${count}`}
                                    aria-current={index === current ? 'true' : undefined}
                                    className={cn(
                                        'h-2 rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                                        index === current ? 'w-6 bg-foreground' : 'w-2 bg-foreground/30 hover:bg-foreground/50'
                                    )}
                                />
                            ))}
                        </div>

                        <button type='button' onClick={() => show(current + 1)} aria-label='Next testimonial' className={controlClasses}>
                            <ChevronRight className='size-5' aria-hidden='true' />
                        </button>
                    </div>
                )}
            </div>
        </section>
    )
}
